import React, { useEffect, useState, useCallback, useLayoutEffect, useRef, useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Grid } from '@material-ui/core';
import { useHistory } from 'react-router';
import { useRouteMatch } from 'react-router-dom';
import { clearCascoForm, setLanguage } from '../../redux/modules/cascoClaim';
import { clearFileUrls } from '../../helpers/uploadHelpers';
import Loader from '../../components/Loaders/Loader';
import { CLAIM_TYPES } from '../../constants';
import useDeviceResize from '../../hooks/useDeviceResize';
import { getCountries, getStates } from '../../redux/modules/countries';
import SuccessScreen from '../../components/SuccessApplication';
import claimSteps from './claimSteps';
import elementScroll from '../../helpers/elementScroll';
import WizardWrapper from '../../components/WizardWrapper';
import EfesContentHeader from '../../components/EfesContentHeader';
import { clearApplicantSearch } from '../../redux/modules/searchApplicant';
import ScrollToTopPage from '../../components/ScrollToTopPage';
import { bindClaimConclusionSuccessTableData } from '../../helpers/claims/cascoClaim';

import CascoCarIcon from '../../img/claims/cascoCar.png';
import useStyles from '../../components/MUI/StepNavigationAmount/makeStyles';

const CascoCompensationPage = () => {
  const { i18n } = useTranslation();

  const dispatch = useDispatch();
  const history = useHistory();
  const match = useRouteMatch();

  const currentLanguage = useRef(i18n.language);
  const isDeviceLgSize = useDeviceResize('lg');
  const classes = useStyles();

  const [currentStep, setCurrentStep] = useState(claimSteps[0]);
  const [unlockedSteps, setUnlockedSteps] = useState([claimSteps[0].id]);
  const [stepNavigationBox, setStepNavigationBox] = useState({});

  const cascoClaim = useSelector((store) => store.cascoClaim, shallowEqual);

  useLayoutEffect(() => {
    dispatch(setLanguage(currentLanguage.current));
  }, [dispatch]);

  useEffect(() => () => {
    dispatch(clearCascoForm());
    dispatch(clearApplicantSearch());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getCountries());
    dispatch(getStates());
  }, [dispatch]);

  useEffect(() => {
    if (cascoClaim.submitSuccess && cascoClaim.attachments) {
      clearFileUrls(cascoClaim.attachments.files);
    }
  }, [cascoClaim]);

  const goToStep = useCallback((newStep) => history.push(`${match.url}/${newStep.id}`), [
    history,
    match.url,
  ]);
  const scrollTo = useCallback((elem) => elementScroll(elem), []);

  const stepParams = {
    type: CLAIM_TYPES.CASCO,
    loader: <Loader />,
    hasLanguageSensitiveNames: true,
    setStepNavigationBox,
    formLanguage: cascoClaim.language,
    scrollTo,
  };

  const startNew = () => {
    dispatch(clearApplicantSearch());
    dispatch(clearCascoForm());
    setCurrentStep(claimSteps[0]);
    setUnlockedSteps([claimSteps[0].id]);
  };

  const claimSuccessData = useMemo(
    () => {
      if (cascoClaim.submitSuccess) {
        return bindClaimConclusionSuccessTableData(cascoClaim);
      }
      return undefined;
    },
    [cascoClaim],
  );

  if (cascoClaim.submitSuccess) {
    return (
      <>
        <EfesContentHeader headerImage={CascoCarIcon} headerText="claim.cascoClaimHeaderText" />
        <SuccessScreen startNewHandler={startNew} claimSuccessData={claimSuccessData} />
      </>
    );
  }

  return (
    <>
      <ScrollToTopPage withDelay />
      <EfesContentHeader headerImage={CascoCarIcon} headerText="claim.cascoClaimHeaderText" />
      <WizardWrapper
        steps={claimSteps}
        stepParams={stepParams}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        unlockedSteps={unlockedSteps}
        setUnlockedSteps={setUnlockedSteps}
        goToStep={goToStep}
      />
      <Grid
        container
        spacing={3}
        className={cn({
          [classes.mobileNavigation]: isDeviceLgSize,
        })}
      >
        <Grid item xs={12}>
          {!isEmpty(stepNavigationBox) && stepNavigationBox}
        </Grid>
      </Grid>
    </>
  );
};

export default CascoCompensationPage;
